/**
 * The checkout's reading of the server's scheduled-menu-pricing refusals
 * (Bar v2 Part D2). With a tenant's `initiative.scheduled_menu_pricing` on, the
 * server prices each cart line on the route that prices it NOW and refuses —
 * before any card is touched —
 *   - SCHEDULED_PRICE_CHANGED: a line's price is not that route's ("Happy Hour
 *     ended — Tito's is now $9.00"), carrying the old price;
 *   - ITEM_NOT_AVAILABLE_NOW: no open route reaches a line ("Tito's is only
 *     available during Happy Hour (Mon–Thu 3–6 PM)").
 * The server writes the sentences (`describeScheduledPriceChange`,
 * `describeItemUnavailableNow` in @order-up/models — the same ones the
 * storefront shows), so this only lists them. The guest confirms HERE and the
 * server's answer is written to the cart as-is (`useApplyServerReprice` →
 * `applyServerRepricing`): the server is authoritative, so a guest whose clock
 * disagrees with it is never looped through a second refusal.
 */
import { formatScheduledPrice, type CartLineRefusal } from '@order-up/models';

export const SCHEDULED_CART_CODES = ['SCHEDULED_PRICE_CHANGED', 'ITEM_NOT_AVAILABLE_NOW'] as const;

export interface ScheduledCartNotice {
  readonly title: string;
  /** The server's refused lines, exactly as sent — what confirming applies. */
  readonly refusals: readonly CartLineRefusal[];
  readonly lines: ReadonlyArray<{ readonly key: string; readonly text: string; readonly detail?: string }>;
}

interface RefusedLine {
  readonly kind?: string;
  readonly cartItemId?: string;
  readonly name?: string;
  readonly message?: string;
  readonly clientUnitPrice?: number;
}

/** The notice for a scheduled-pricing refusal, or `null` for any other error. */
export function scheduledCartNoticeFrom(error: unknown): ScheduledCartNotice | null {
  const e = error as { code?: string; message?: string; details?: { lines?: unknown } } | null;
  if (!e?.code || !(SCHEDULED_CART_CODES as readonly string[]).includes(e.code)) return null;
  const refused = Array.isArray(e.details?.lines) ? (e.details!.lines as RefusedLine[]) : [];
  const lines = refused
    .filter((l) => l.kind === 'scheduled_price_changed' || l.kind === 'not_available_now')
    .map((l, i) => ({
      key: l.cartItemId ?? String(i),
      text: l.message ?? l.name ?? 'An item in your cart changed',
      ...(l.kind === 'scheduled_price_changed' && typeof l.clientUnitPrice === 'number'
        ? { detail: `was ${formatScheduledPrice(l.clientUnitPrice)}` }
        : l.kind === 'not_available_now'
          ? { detail: 'It will be removed from your cart.' }
          : {}),
    }));
  return {
    refusals: refused as CartLineRefusal[],
    title: e.code === 'ITEM_NOT_AVAILABLE_NOW' ? "Something in your cart isn't available right now" : 'Some prices have changed',
    lines:
      lines.length > 0
        ? lines
        : [{ key: 'message', text: (e.message ?? '').replace(/^[A-Z_]+: /, '') || 'Your cart changed.' }],
  };
}
